import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Option,
  Select,
  Stack,
  Typography,
  Sheet,
  Tooltip,
  Menu,
  MenuItem,
  Switch,
  CircularProgress,
} from '@mui/joy'
import {
  Add,
  Edit,
  Person,
  AdminPanelSettings,
  SupervisorAccount,
  Badge as BadgeIcon,
  Check,
  Remove,
  MoreVert,
  Visibility,
  VisibilityOff,
  LockReset,
} from '@mui/icons-material'
import { useAuth } from '@/context/AuthContext'
import { userService } from '@/services/userService'
import { shiftTypeService } from '@/services/shiftTypeService'
import { toast } from 'react-hot-toast'
import { User } from '@/types/user'
import { ShiftType } from '@/types/shiftType'

interface UserFormData {
  email: string
  password: string
  first_name: string
  last_name: string
  role: 'admin' | 'manager' | 'employee'
  phone?: string
  employee_id: string
}



const initialFormData: UserFormData = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'employee',
  phone: '',
  employee_id: '',
}

export default function UsersPage() {
  const { permissions, user: currentUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([])
  const [userShiftTypes, setUserShiftTypes] = useState<Record<number, ShiftType[]>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isShiftTypeDialogOpen, setIsShiftTypeDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [formData, setFormData] = useState<UserFormData>(initialFormData)
  const [editFormData, setEditFormData] = useState<UserFormData>(initialFormData)
  const [errors, setErrors] = useState<Partial<UserFormData>>({})
  const [selectedShiftTypes, setSelectedShiftTypes] = useState<number[]>([])
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [selectedUserForMenu, setSelectedUserForMenu] = useState<User | null>(null)
  const [showInactiveUsers, setShowInactiveUsers] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
  const [resetLinkUser, setResetLinkUser] = useState<User | null>(null)
  const [isSendingResetLink, setIsSendingResetLink] = useState(false)

  // Helper function to check permissions
  const hasPermission = (feature: string, action: string) => {
    return permissions.includes(`${feature}.${action}`)
  }

  // Check if user can create users (admin only)
  const canCreateUsers = hasPermission('users', 'create')
  
  // Check if user can assign shift types (manager or admin)
  const canAssignShiftTypes = hasPermission('shift_types', 'assign')
  
  // Check if user can edit users (admin only)
  const canEditUsers = hasPermission('users', 'edit')
  
  // Check if user can delete users (admin only)
  const canDeleteUsers = hasPermission('users', 'delete')
  
  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin'

  // Fetch users and shift types
  useEffect(() => {
    const fetchData = async () => {
      // Prevent multiple simultaneous requests
      if (isRefreshing || dataLoaded) return;
      
      try {
        setIsLoading(true);
        const [usersData, shiftTypesData] = await Promise.all([
          userService.getAllUsers(true), // Always fetch all users including inactive
          shiftTypeService.getAllShiftTypes(),
        ])
        setUsers(usersData)
        setShiftTypes(shiftTypesData)
        
        // Only fetch shift types for employees and managers, and only if we haven't loaded them yet
        const employeesAndManagers = usersData.filter(user => 
          (user.role === 'employee' || user.role === 'manager') && 
          !userShiftTypes[user.id] // Only fetch if not already cached
        )
        
        if (employeesAndManagers.length > 0) {
          setIsRefreshing(true);
          
          try {
            // Fetch shift types for all employees/managers in a single batch call
            const userIds = employeesAndManagers.map(user => user.id)
            const batchShiftTypes = await userService.getUsersShiftTypes(userIds)
            
            // Update the shift types map
            setUserShiftTypes(prev => ({
              ...prev,
              ...batchShiftTypes
            }))
          } catch (error) {
            console.error('Failed to load shift types for users:', error)
            toast.error('Failed to load shift types for some users')
          } finally {
            setIsRefreshing(false)
          }
        }
        
        setDataLoaded(true)
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to load data')
      } finally {
        setIsLoading(false)
      }
    }

    // Only fetch if we have permissions and haven't loaded data yet
    if (permissions.length > 0 && !dataLoaded) {
      fetchData()
    }
  }, [permissions, dataLoaded, userShiftTypes, isRefreshing])

  const handleOpenCreateDialog = () => {
    setFormData(initialFormData)
    setErrors({})
    setIsCreateDialogOpen(true)
  }

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false)
    setFormData(initialFormData)
    setErrors({})
  }

  const handleOpenShiftTypeDialog = (user: User) => {
    setSelectedUser(user)
    // Pre-select already assigned shift types
    const assignedShiftTypes = userShiftTypes[user.id] || []
    setSelectedShiftTypes(assignedShiftTypes.map(st => st.id))
    setIsShiftTypeDialogOpen(true)
  }

  const handleCloseShiftTypeDialog = () => {
    setIsShiftTypeDialogOpen(false)
    setSelectedUser(null)
    setSelectedShiftTypes([])
  }

  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user)
    setEditFormData({
      email: user.email,
      password: '', // Don't populate password
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      phone: user.phone || '',
      employee_id: user.employee_id || '',
    })
    setErrors({})
    setIsEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false)
    setSelectedUser(null)
    setEditFormData(initialFormData)
    setErrors({})
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setMenuAnchor(event.currentTarget)
    setSelectedUserForMenu(user)
  }

  const handleCloseMenu = () => {
    setMenuAnchor(null)
    setSelectedUserForMenu(null)
  }

  const handleDeactivateUser = async () => {
    if (!selectedUserForMenu) return

    try {
      await userService.deleteUser(selectedUserForMenu.id)
      setUsers(users.map(u => u.id === selectedUserForMenu.id ? { ...u, is_active: false } : u))
      toast.success('User deactivated successfully')
      handleCloseMenu()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to deactivate user')
    }
  }

  const handleActivateUser = async () => {
    if (!selectedUserForMenu) return

    try {
      await userService.activateUser(selectedUserForMenu.id)
      setUsers(users.map(u => u.id === selectedUserForMenu.id ? { ...u, is_active: true } : u))
      toast.success('User activated successfully')
      handleCloseMenu()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to activate user')
    }
  }

  const handleRemoveShiftType = async (userId: number, shiftTypeId: number) => {
    if (isRefreshing) return; // Prevent multiple simultaneous requests
    
    try {
      setIsRefreshing(true);
      await userService.removeShiftTypeFromUser(userId, shiftTypeId)
      setUserShiftTypes(prev => ({
        ...prev,
        [userId]: prev[userId]?.filter(st => st.id !== shiftTypeId) || []
      }))
      toast.success('Shift type removed successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove shift type')
    } finally {
      setIsRefreshing(false);
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<UserFormData> = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    if (!formData.first_name) {
      newErrors.first_name = 'First name is required'
    }

    if (!formData.last_name) {
      newErrors.last_name = 'Last name is required'
    }

    if (!formData.role) {
      newErrors.role = 'Role is required' as any
    }

    if (!formData.employee_id) {
      newErrors.employee_id = 'Employee ID is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCreateUser = async () => {
    if (!validateForm() || isRefreshing) return

    try {
      setIsRefreshing(true);
      const newUser = await userService.createUser(formData)
      setUsers([...users, newUser])
      toast.success('User created successfully')
      handleCloseCreateDialog()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to create user'
      
      // Handle specific backend errors
      if (errorMessage.includes('employee_id') || errorMessage.includes('Employee ID')) {
        setErrors(prev => ({ ...prev, employee_id: 'Employee ID must be unique' }))
      } else if (errorMessage.includes('email') || errorMessage.includes('Email')) {
        setErrors(prev => ({ ...prev, email: 'Email must be unique' }))
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsRefreshing(false);
    }
  }

  const handleUpdateUser = async () => {
    if (!selectedUser || isRefreshing) return

    // Validate form (excluding password if empty)
    const newErrors: Partial<UserFormData> = {}
    if (!editFormData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(editFormData.email)) {
      newErrors.email = 'Email is invalid'
    }
    if (!editFormData.first_name) {
      newErrors.first_name = 'First name is required'
    }
    if (!editFormData.last_name) {
      newErrors.last_name = 'Last name is required'
    }
    if (!editFormData.role) {
      newErrors.role = 'Role is required' as any
    }
    if (!editFormData.employee_id) {
      newErrors.employee_id = 'Employee ID is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      setIsRefreshing(true);
      const updateData = { ...editFormData }
      if (!updateData.password) {
        const { password, ...dataWithoutPassword } = updateData
        const updatedUser = await userService.updateUser(selectedUser.id, dataWithoutPassword)
        setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u))
        toast.success('User updated successfully')
        handleCloseEditDialog()
        return
      }
      
      const updatedUser = await userService.updateUser(selectedUser.id, updateData)
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u))
      toast.success('User updated successfully')
      handleCloseEditDialog()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update user'
      
      // Handle specific backend errors
      if (errorMessage.includes('employee_id') || errorMessage.includes('Employee ID')) {
        setErrors(prev => ({ ...prev, employee_id: 'Employee ID must be unique' }))
      } else if (errorMessage.includes('email') || errorMessage.includes('Email')) {
        setErrors(prev => ({ ...prev, email: 'Email must be unique' }))
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsRefreshing(false);
    }
  }

  const handleAssignShiftTypes = async () => {
    if (!selectedUser || isRefreshing) return; // Prevent multiple simultaneous requests

    try {
      setIsRefreshing(true);
      await userService.assignShiftTypesToUser(selectedUser.id, selectedShiftTypes)
      
      // Update local state
      setUserShiftTypes(prev => ({
        ...prev,
        [selectedUser.id]: shiftTypes.filter(st => selectedShiftTypes.includes(st.id))
      }))
      
      toast.success('Shift types assigned successfully')
      handleCloseShiftTypeDialog()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to assign shift types')
    } finally {
      setIsRefreshing(false);
    }
  }

  const handleOpenResetDialog = (user: User) => {
    setResetLinkUser(user)
    setIsResetDialogOpen(true)
    handleCloseMenu()
  }

  const handleCloseResetDialog = () => {
    if (!isSendingResetLink) {
      setIsResetDialogOpen(false)
      setResetLinkUser(null)
    }
  }

  const handleSendResetLink = async () => {
    if (!resetLinkUser) return

    setIsSendingResetLink(true)
    try {
      await userService.sendPasswordResetLink(resetLinkUser.id)
      toast.success(`Reset link sent to ${resetLinkUser.first_name} ${resetLinkUser.last_name}.`)
      setIsResetDialogOpen(false)
      setResetLinkUser(null)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send reset link')
    } finally {
      setIsSendingResetLink(false)
    }
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings />
      case 'manager':
        return <SupervisorAccount />
      case 'employee':
        return <BadgeIcon />
      default:
        return <Person />
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'danger'
      case 'manager':
        return 'warning'
      case 'employee':
        return 'primary'
      default:
        return 'neutral'
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading users...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography level="h1" mb={1}>User Management</Typography>
          <Typography level="body-sm" color="neutral">
            Manage users and their roles
            {isRefreshing && (
              <Typography level="body-xs" color="primary" ml={1}>
                (Refreshing...)
              </Typography>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography level="body-sm">Show inactive users</Typography>
            <Switch
              checked={showInactiveUsers}
              onChange={(e) => setShowInactiveUsers(e.target.checked)}
              size="sm"
            />
          </Stack>
          {canCreateUsers && (
            <Button
              startDecorator={<Add />}
              onClick={handleOpenCreateDialog}
              size="sm"
            >
              Create User
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            {users
              .filter(user => showInactiveUsers || user.is_active)
              .map((user) => (
              <Card key={user.id} variant="outlined" sx={{ 
                opacity: user.is_active ? 1 : 0.6,
                borderColor: user.is_active ? undefined : 'neutral.300'
              }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center">
                      {getRoleIcon(user.role)}
                      <Box>
                        <Typography level="body-sm" fontWeight="lg">
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          {user.email}
                        </Typography>
                        {user.phone && (
                          <Typography level="body-xs" color="neutral">
                            {user.phone}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    
                                         <Stack direction="row" spacing={2} alignItems="center">
                       <Chip
                         size="sm"
                         color={getRoleColor(user.role)}
                         variant="soft"
                       >
                         {user.role}
                       </Chip>
                       
                                               <Chip
                          color={user.is_active ? 'success' : 'danger'}
                          variant="soft"
                          size="sm"
                          startDecorator={user.is_active ? <Visibility /> : <VisibilityOff />}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Chip>
                       
                       {user.employee_id && (
                         <Typography level="body-xs" color="neutral">
                           ID: {user.employee_id}
                         </Typography>
                       )}
                       
                                               {/* Show assigned shift types for employees and managers */}
                        {(user.role === 'employee' || user.role === 'manager') && userShiftTypes[user.id] && userShiftTypes[user.id].length > 0 && (
                         <Stack direction="row" spacing={1} alignItems="center">
                           <Typography level="body-xs" color="neutral">
                             Shift Types:
                           </Typography>
                           {userShiftTypes[user.id].map((shiftType) => (
                             <Chip
                               key={shiftType.id}
                               size="sm"
                               variant="outlined"
                               sx={{ backgroundColor: shiftType.color + '20' }}
                             >
                               <Box
                                 sx={{
                                   width: 8,
                                   height: 8,
                                   borderRadius: '50%',
                                   backgroundColor: shiftType.color,
                                   mr: 0.5,
                                 }}
                               />
                               {shiftType.name}
                               {canAssignShiftTypes && (
                                 <IconButton
                                   size="sm"
                                   sx={{ ml: 0.5, p: 0.25 }}
                                   onClick={() => handleRemoveShiftType(user.id, shiftType.id)}
                                 >
                                   <Remove sx={{ fontSize: 12 }} />
                                 </IconButton>
                               )}
                             </Chip>
                           ))}
                         </Stack>
                       )}
                       
                                               {/* Action buttons */}
                        <Stack direction="row" spacing={1}>
                          {canAssignShiftTypes && (user.role === 'employee' || user.role === 'manager') && user.is_active && (
                            <Tooltip title="Assign Shift Types">
                              <IconButton
                                size="sm"
                                onClick={() => handleOpenShiftTypeDialog(user)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {(canEditUsers || canDeleteUsers) && (
                            <Tooltip title="More options">
                              <IconButton
                                size="sm"
                                onClick={(e) => handleOpenMenu(e, user)}
                              >
                                <MoreVert />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                     </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Modal open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
        <ModalDialog size="lg">
          <DialogTitle>Create New User</DialogTitle>
          <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid xs={6}>
                <FormControl error={!!errors.first_name}>
                  <Typography level="body-sm" mb={1}>First Name *</Typography>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Enter first name"
                  />
                  {errors.first_name && (
                    <FormHelperText>{errors.first_name}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl error={!!errors.last_name}>
                  <Typography level="body-sm" mb={1}>Last Name *</Typography>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Enter last name"
                  />
                  {errors.last_name && (
                    <FormHelperText>{errors.last_name}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <FormControl error={!!errors.email}>
              <Typography level="body-sm" mb={1}>Email *</Typography>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
              />
              {errors.email && (
                <FormHelperText>{errors.email}</FormHelperText>
              )}
            </FormControl>

            <FormControl error={!!errors.password}>
              <Typography level="body-sm" mb={1}>Password *</Typography>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
              />
              {errors.password && (
                <FormHelperText>{errors.password}</FormHelperText>
              )}
            </FormControl>

            <FormControl error={!!errors.role}>
              <Typography level="body-sm" mb={1}>Role *</Typography>
              <Select
                value={formData.role}
                onChange={(_, value) => setFormData({ ...formData, role: value as any })}
                placeholder="Select role"
              >
                <Option value="manager">Manager</Option>
                <Option value="employee">Employee</Option>
              </Select>
              {errors.role && (
                <FormHelperText>{errors.role}</FormHelperText>
              )}
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={6}>
                <FormControl>
                  <Typography level="body-sm" mb={1}>Phone</Typography>
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl error={!!errors.employee_id}>
                  <Typography level="body-sm" mb={1}>Employee ID *</Typography>
                  <Input
                    value={formData.employee_id || ''}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="Enter employee ID"
                  />
                  {errors.employee_id && (
                    <FormHelperText>{errors.employee_id}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCloseCreateDialog}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser}>
              Create User
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Assign Shift Types Dialog */}
      <Modal open={isShiftTypeDialogOpen} onClose={handleCloseShiftTypeDialog}>
        <ModalDialog size="lg">
          <DialogTitle>
            Assign Shift Types to {selectedUser?.first_name} {selectedUser?.last_name} ({selectedUser?.role})
          </DialogTitle>
          <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography level="body-sm" color="neutral">
              Select the shift types this employee can work:
            </Typography>
            
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
              <List>
                {shiftTypes.map((shiftType) => (
                  <ListItem key={shiftType.id}>
                    <ListItemDecorator>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: shiftType.color,
                        }}
                      />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">{shiftType.name}</Typography>
                      {shiftType.description && (
                        <Typography level="body-xs" color="neutral">
                          {shiftType.description}
                        </Typography>
                      )}
                    </ListItemContent>
                    <IconButton
                      size="sm"
                      variant={selectedShiftTypes.includes(shiftType.id) ? 'solid' : 'outlined'}
                      color={selectedShiftTypes.includes(shiftType.id) ? 'primary' : 'neutral'}
                      onClick={() => {
                        if (selectedShiftTypes.includes(shiftType.id)) {
                          setSelectedShiftTypes(selectedShiftTypes.filter(id => id !== shiftType.id))
                        } else {
                          setSelectedShiftTypes([...selectedShiftTypes, shiftType.id])
                        }
                      }}
                    >
                      {selectedShiftTypes.includes(shiftType.id) ? <Check /> : <Add />}
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Sheet>
          </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCloseShiftTypeDialog}>
              Cancel
            </Button>
                      <Button 
            onClick={handleAssignShiftTypes}
          >
            {selectedShiftTypes.length > 0 ? 'Update Shift Types' : 'Clear All Shift Types'}
          </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Edit User Dialog */}
      <Modal open={isEditDialogOpen} onClose={handleCloseEditDialog}>
        <ModalDialog size="lg">
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid xs={6}>
                  <FormControl error={!!errors.first_name}>
                    <Typography level="body-sm" mb={1}>First Name *</Typography>
                    <Input
                      value={editFormData.first_name}
                      onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                      placeholder="Enter first name"
                    />
                    {errors.first_name && (
                      <FormHelperText>{errors.first_name}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid xs={6}>
                  <FormControl error={!!errors.last_name}>
                    <Typography level="body-sm" mb={1}>Last Name *</Typography>
                    <Input
                      value={editFormData.last_name}
                      onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                      placeholder="Enter last name"
                    />
                    {errors.last_name && (
                      <FormHelperText>{errors.last_name}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl error={!!errors.email}>
                <Typography level="body-sm" mb={1}>Email *</Typography>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <FormHelperText>{errors.email}</FormHelperText>
                )}
              </FormControl>

              <FormControl>
                <Typography level="body-sm" mb={1}>Password (leave blank to keep current)</Typography>
                <Input
                  type="password"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                  placeholder="Enter new password"
                />
              </FormControl>

              {isAdmin && (
                <FormControl error={!!errors.role}>
                  <Typography level="body-sm" mb={1}>Role *</Typography>
                  <Select
                    value={editFormData.role}
                    onChange={(_, value) => setEditFormData({ ...editFormData, role: value as any })}
                    placeholder="Select role"
                  >
                    <Option value="manager">Manager</Option>
                    <Option value="employee">Employee</Option>
                  </Select>
                  {errors.role && (
                    <FormHelperText>{errors.role}</FormHelperText>
                  )}
                </FormControl>
              )}

              <Grid container spacing={2}>
                <Grid xs={6}>
                  <FormControl>
                    <Typography level="body-sm" mb={1}>Phone</Typography>
                    <Input
                      value={editFormData.phone || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </FormControl>
                </Grid>
                <Grid xs={6}>
                  <FormControl error={!!errors.employee_id}>
                    <Typography level="body-sm" mb={1}>Employee ID *</Typography>
                    <Input
                      value={editFormData.employee_id || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, employee_id: e.target.value })}
                      placeholder="Enter employee ID"
                    />
                    {errors.employee_id && (
                      <FormHelperText>{errors.employee_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser}>
              Update User
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* User Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        placement="bottom-end"
      >
        {canEditUsers && selectedUserForMenu && selectedUserForMenu.role !== 'admin' && (
          <MenuItem onClick={() => {
            handleOpenEditDialog(selectedUserForMenu)
            handleCloseMenu()
          }}>
            <ListItemDecorator>
              <Edit />
            </ListItemDecorator>
            Edit User
          </MenuItem>
        )}
        {canEditUsers && selectedUserForMenu && selectedUserForMenu.id !== currentUser?.id && (
          <MenuItem onClick={() => handleOpenResetDialog(selectedUserForMenu)}>
            <ListItemDecorator>
              <LockReset />
            </ListItemDecorator>
            Send reset link
          </MenuItem>
        )}
        {canDeleteUsers && selectedUserForMenu && selectedUserForMenu.id !== currentUser?.id && (
          selectedUserForMenu.is_active ? (
            <MenuItem 
              onClick={handleDeactivateUser}
              color="danger"
            >
              <ListItemDecorator>
                <Remove />
              </ListItemDecorator>
              Deactivate User
            </MenuItem>
          ) : (
            <MenuItem 
              onClick={handleActivateUser}
              color="success"
            >
              <ListItemDecorator>
                <Check />
              </ListItemDecorator>
              Activate User
            </MenuItem>
          )
        )}
      </Menu>

      {/* Send Password Reset Link Dialog */}
      <Modal open={isResetDialogOpen} onClose={handleCloseResetDialog}>
        <ModalDialog>
          <DialogTitle>Send password reset link?</DialogTitle>
          <DialogContent>
            <Typography level="body-md">
              A password reset email will be sent to{' '}
              <strong>{resetLinkUser?.first_name} {resetLinkUser?.last_name}</strong> at{' '}
              <strong>{resetLinkUser?.email}</strong>. They will be required to set a new password
              before logging in.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseResetDialog}
              disabled={isSendingResetLink}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendResetLink}
              disabled={isSendingResetLink}
              startDecorator={isSendingResetLink ? <CircularProgress size="sm" /> : undefined}
            >
              {isSendingResetLink ? 'Sending...' : 'Send Link'}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  )
} 